import { z } from "zod";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import { zQueryUuidArray } from "@common/schemas/query-uuid-array.schema";
import { dbIntIdSchema } from "@common/schemas/shared.schema";
import { CatalogItemFilterType } from "@common/types/catalogItem";
import {
    InvoiceTemplateArchivedFilter,
    InvoiceTemplateSort,
} from "@common/types/invoiceTemplate";
import { nativeInvoiceAdjustmentSettingsResponseSchema } from "./native-invoice-adjustment-settings.schema";
import {
    nativeInvoiceCustomTermSchema,
    refineNativeInvoiceDueDateRule,
} from "./native-invoice-template-intent.schema";
import {
    nativeInvoiceAcceptedPaymentMethodsSchema,
    nativeInvoiceDraftBodyObjectSchema,
    nativeInvoiceLineAccountingSchema,
    nativeInvoiceRenderSnapshotSchema,
    nativeInvoiceStoredEmailSnapshotSchema,
} from "./native-invoice.schema";

export const INVOICE_TEMPLATE_NAME_MAX_LENGTH = 255;

// Upper bound on a single list request's `limit`. Shared with the frontend so
// the per-entity fan-out never asks for a depth the backend would reject with
// a 400 (see useInvoiceTemplatesForEntities). Mirrors listCatalogItemsQuery.
export const INVOICE_TEMPLATE_LIST_MAX_LIMIT = 200;

export const invoiceTemplateNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(INVOICE_TEMPLATE_NAME_MAX_LENGTH);

export const createInvoiceTemplateFromInvoiceBodySchema = z.object({
    name: invoiceTemplateNameSchema,
});

export const renameInvoiceTemplateBodySchema = z.object({
    name: invoiceTemplateNameSchema,
});

// Query-string serialization collapses a single-element array to a scalar,
// so wrap a lone id back into an array (mirrors zQueryUuidArray).
// `dbIntIdSchema` bounds each id to the positive int4 range so an out-of-range
// value fails validation instead of reaching the `IN` predicate.
const CreatedByUserIdArraySchema = z.preprocess(
    (value) =>
        typeof value === "string" || typeof value === "number"
            ? [value]
            : value,
    z.array(dbIntIdSchema),
);

export const listInvoiceTemplatesQuerySchema = z
    .object({
        search: z.string().max(255).optional(),
        archivedFilter: z
            .nativeEnum(InvoiceTemplateArchivedFilter)
            .optional()
            .default(InvoiceTemplateArchivedFilter.ACTIVE),
        counterpartiesIds: zQueryUuidArray().optional(),
        withEmptyOrUnverifiedCounterparty: zQueryBoolean().optional(),
        hasCounterparty: zQueryBoolean().optional(),
        createdByUserIds: CreatedByUserIdArraySchema.optional(),
        // Templates whose prototype invoice has a line using these catalog
        // items; match mode mirrors the invoices list (defaults to any).
        catalogItemIds: zQueryUuidArray().optional(),
        catalogItemFilterType: z.nativeEnum(CatalogItemFilterType).optional(),
        // Optional single-column sort. Omitted / cleared falls back to the page
        // default (UPDATED_AT_DESC) resolved in the service.
        sort: z.nativeEnum(InvoiceTemplateSort).optional(),
        // Both omitted = unpaginated (full matching set, up to the existing
        // MAX_LIST_INVOICE_TEMPLATES cap) so existing callers (e.g. the invoice
        // drawer's template selector) are unaffected. Only the templates list
        // page passes both to page through results. Mirrors
        // listCatalogItemsQuerySchema (catalogItem.schema.ts).
        page: z.coerce.number().int().min(1).optional(),
        limit: z.coerce
            .number()
            .int()
            .min(1)
            .max(INVOICE_TEMPLATE_LIST_MAX_LIMIT)
            .optional(),
    })
    // page/limit are strictly both-or-nothing: passing only one is a caller
    // error, not a silent fall-through to the unpaginated set.
    .refine(
        (value) => (value.page === undefined) === (value.limit === undefined),
        { message: "page and limit must be provided together" },
    );

const invoiceTemplateContentBodyObjectSchema =
    nativeInvoiceDraftBodyObjectSchema.omit({
        status: true,
        revisedFromInvoiceId: true,
        billableExpenseTransactionIds: true,
        sourceTemplateId: true,
    });

export const invoiceTemplateContentBodySchema =
    invoiceTemplateContentBodyObjectSchema.superRefine(
        refineNativeInvoiceDueDateRule,
    );

export const createInvoiceTemplateBodySchema =
    invoiceTemplateContentBodyObjectSchema
        .extend({
            name: invoiceTemplateNameSchema,
        })
        .superRefine(refineNativeInvoiceDueDateRule);

export const invoiceTemplateMetadataSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number().int(),
    name: z.string(),
    archivedAt: z.coerce.date().nullable(),
    createdByUserId: z.number().int().nullable(),
    createdAt: z.coerce.date(),
    updatedByUserId: z.number().int().nullable(),
    updatedAt: z.coerce.date(),
    archivedByUserId: z.number().int().nullable(),
});

export const invoiceTemplateListItemSchema =
    invoiceTemplateMetadataSchema.extend({
        customerName: z.string().nullable(),
        total: z.number().nullable(),
        currency: z.string().nullable(),
        // True once at least one real invoice has been created from this
        // template (sourceTemplateId stamped on native invoice create).
        // Used templates cannot be deleted — only archived.
        hasSourcedInvoices: z.boolean(),
        // Number of invoices created from this template (sourceTemplateId).
        sourcedInvoiceCount: z.number().int().min(0),
        // Creation time of the most recent invoice created from this template
        // (any currency); null when the template has never been used.
        lastUsedAt: z.coerce.date().nullable(),
    });

export const invoiceTemplateDetailSchema = invoiceTemplateMetadataSchema.extend(
    {
        counterpartyId: z.string().uuid().nullable(),
        counterpartyCc: z.string().nullable(),
        counterpartyBcc: z.string().nullable(),
        financialAccountId: z.number().int().nullable(),
        collectPayment: z.boolean(),
        stripeFinancialAccountId: z.number().int().nullable(),
        acceptedPaymentMethods: nativeInvoiceAcceptedPaymentMethodsSchema,
        sendingEmailId: z.string().uuid().nullable(),
        termType: z.nativeEnum(NativeInvoiceTermType).nullable(),
        customTerm: nativeInvoiceCustomTermSchema.nullable(),
        isEmailSubjectAutomatic: z.boolean(),
        isEmailMessageAutomatic: z.boolean(),
        renderSnapshot: nativeInvoiceRenderSnapshotSchema,
        emailSnapshot: nativeInvoiceStoredEmailSnapshotSchema.nullable(),
        lineAccounting: nativeInvoiceLineAccountingSchema,
        adjustmentSettings: nativeInvoiceAdjustmentSettingsResponseSchema,
    },
);

export type CreateInvoiceTemplateFromInvoiceBody = z.infer<
    typeof createInvoiceTemplateFromInvoiceBodySchema
>;
export type CreateInvoiceTemplateBody = z.infer<
    typeof createInvoiceTemplateBodySchema
>;
export type RenameInvoiceTemplateBody = z.infer<
    typeof renameInvoiceTemplateBodySchema
>;
export type ListInvoiceTemplatesQuery = z.infer<
    typeof listInvoiceTemplatesQuerySchema
>;
export type InvoiceTemplateContentBody = z.infer<
    typeof invoiceTemplateContentBodySchema
>;
export type InvoiceTemplateMetadata = z.infer<
    typeof invoiceTemplateMetadataSchema
>;
export type InvoiceTemplateListItem = z.infer<
    typeof invoiceTemplateListItemSchema
>;
export type InvoiceTemplateDetail = z.infer<typeof invoiceTemplateDetailSchema>;
